import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { nsConfirm } from '../shared/ns-confirm'
import { useBranding } from '../../context/BrandingContext'
import '../../styles/client-pages.css'

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const IcShield  = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
const IcCheck   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
const IcHelp    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
const IcGlobe   = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
const IcPlus    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
const IcTrash   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
const IcExternal = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
const IcRefresh = ({ spin }) => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={spin ? { animation: 'spin 0.8s linear infinite' } : undefined}><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
const IcX       = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
const IcCopy    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
const IcBook    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
const IcLock    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
const IcStar    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>

// ── Health card (prototype ClHealth) ───────────────────────────────────────────
function ClHealth({ title, value, sub, ok, warn }) {
  return (
    <div className="card card-pad-sm">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span className={`icon-tile ${ok ? 'success' : 'warning'}`} style={{ width: 26, height: 26, fontSize: 12 }}>
          {ok ? <IcCheck /> : <IcHelp />}
        </span>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{title}</div>
      </div>
      <div style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', color: warn ? 'var(--warning)' : 'var(--text)' }}>{value}</div>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 }}>{sub}</div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
const DomainManagement = () => {
  const { cmsName } = useBranding()
  const [showAddDomain, setShowAddDomain] = useState(false)
  const [newDomain, setNewDomain]         = useState('')
  const [checkResult, setCheckResult]     = useState(null)
  const [showGuide, setShowGuide]         = useState(false)
  const [copiedIdx, setCopiedIdx]         = useState(null)
  const queryClient = useQueryClient()

  // ── Preserved API calls ───────────────────────────────────────────────────
  const { data: domainsResponse, isLoading, error } = useQuery(
    'client-domains',
    () => apiService.get('/domains').then(res => res.data),
    {
      staleTime: 30000,
      refetchInterval: 60000,
      retry: (failureCount, err) => {
        if (err?.response?.status === 401) return false
        return failureCount < 2
      }
    }
  )

  const addDomainMutation = useMutation(
    (domain) => apiService.post('/domains', { domain }),
    {
      onSuccess: () => {
        toast.success('Domain added successfully!')
        queryClient.invalidateQueries('client-domains')
        setShowAddDomain(false)
        setNewDomain('')
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to add domain')
    }
  )

  const checkConnectionMutation = useMutation(
    (id) => apiService.post(`/domains/${id}/check-connection`).then(res => res.data),
    {
      onSuccess: (data) => {
        setCheckResult(data)
        queryClient.invalidateQueries('client-domains')
        if (data.ok) toast.success('All records found on your nameservers — you\'re connected')
        else toast(`${data.records.filter(r => r.status !== 'ok').length} record(s) not visible yet — DNS can take up to 24h`, { icon: '⏳' })
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Connection check failed')
    }
  )

  const setPrimaryMutation = useMutation(
    (id) => apiService.patch(`/domains/${id}/set-primary`),
    {
      onSuccess: (_res, _id) => {
        toast.success('Default domain updated')
        queryClient.invalidateQueries('client-domains')
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to set default domain')
    }
  )

  const deleteDomainMutation = useMutation(
    (id) => apiService.delete(`/domains/${id}`),
    {
      onSuccess: () => {
        toast.success('Domain removed')
        queryClient.invalidateQueries('client-domains')
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to remove domain')
    }
  )

  // ── Data ──────────────────────────────────────────────────────────────────
  const domains = Array.isArray(domainsResponse?.domains)
    ? domainsResponse.domains
    : (Array.isArray(domainsResponse) ? domainsResponse : [])
  // The default domain (real is_primary flag) drives the hero, "Visit site",
  // DNS records and connection check; fall back to the first domain.
  const primaryDomain = domains.find(d => d.is_primary) || (domains.length > 0 ? domains[0] : null)
  // Show the default first in the list.
  const sortedDomains = [...domains].sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0))
  const domainName    = primaryDomain?.domain || primaryDomain?.name || 'yourdomain.com'
  const sslValid      = primaryDomain?.ssl_status === 'valid' || primaryDomain?.ssl_status === 'active' || primaryDomain?.ssl_enabled
  const isActive      = primaryDomain?.status === 'active' || primaryDomain?.status === 'connected'
  const dnsOk         = isActive || primaryDomain?.dns_configured

  // SSL renewal countdown (real, if the cert expiry is known)
  const sslDays = primaryDomain?.ssl_expires_at
    ? Math.max(0, Math.round((new Date(primaryDomain.ssl_expires_at) - Date.now()) / 86400000))
    : null

  // The exact records the client must add at their registrar (computed by the
  // backend per domain); falls back to a sample set if not connected yet.
  const setupRecords = (Array.isArray(primaryDomain?.dns_setup) && primaryDomain.dns_setup.length)
    ? primaryDomain.dns_setup
    : [
        { type: 'A',     name: '@',   value: '51.104.28.89',      ttl: 'auto' },
        { type: 'CNAME', name: 'www', value: 'cname.webnest.app', ttl: 'auto' },
      ]

  // After a connection check we show the real, resolved records; before that, the
  // illustrative WebNest set with a neutral "not checked" status.
  const tableRows = checkResult?.records?.length
    ? checkResult.records.map(r => ({ type: r.type, name: r.name, value: r.value, status: r.status }))
    : setupRecords.map(r => ({ ...r, status: null }))

  const failingRecords = checkResult?.records?.filter(r => r.status !== 'ok') || []

  const copyValue = (i, value) => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopiedIdx(i)
      setTimeout(() => setCopiedIdx(c => (c === i ? null : c)), 1300)
    }).catch(() => toast.error('Copy failed'))
  }

  const STATUS_BADGE = {
    ok:       { cls: 'badge-success', icon: <IcCheck />, label: 'OK' },
    missing:  { cls: 'badge-warning', icon: <IcHelp />,  label: 'Not found' },
    mismatch: { cls: 'badge-danger',  icon: <IcX />,     label: 'Mismatch' },
  }

  const runCheck = () => primaryDomain?.id && checkConnectionMutation.mutate(primaryDomain.id)
  const checking = checkConnectionMutation.isLoading

  const disconnect = async (d) => {
    const ok = await nsConfirm({
      tone: 'danger', icon: 'domain',
      title: `Disconnect ${d.domain || d.name}?`,
      message: `The domain stops pointing at ${cmsName}. Your site stays, but this address will no longer reach it.`,
      detail: 'DNS changes can take up to 24 hours to take effect',
      confirmLabel: 'Disconnect domain',
    })
    if (ok) deleteDomainMutation.mutate(d.id)
  }

  if (isLoading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>Loading domains…</div>
  }

  if (error && error.response?.status !== 404) {
    return (
      <div className="card card-pad" style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ color: 'var(--danger)', fontSize: 14 }}>Failed to load domain information.</div>
        <button className="btn btn-sm" style={{ marginTop: 12 }} onClick={() => queryClient.invalidateQueries('client-domains')}>Retry</button>
      </div>
    )
  }

  return (
    <>
      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Your account</div>
          <h1>Domain</h1>
          <div className="subtitle">Your web address and security — all in one place.</div>
        </div>
        <div className="flex gap-2 items-center">
          {primaryDomain?.domain && (
            <a href={`https://${primaryDomain.domain}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <button className="btn"><IcExternal /> Visit site</button>
            </a>
          )}
          <button className="btn btn-primary" onClick={() => setShowAddDomain(true)}><IcPlus /> Connect another domain</button>
        </div>
      </div>

      <div className="page-body" style={{ gap: 18 }}>
        {/* Hero domain card */}
        <div className="client-hero" style={{ padding: '26px 30px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <span className="icon-tile" style={{ width: 48, height: 48, fontSize: 22 }}><IcGlobe /></span>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em' }}>{domainName}</span>
                <span className={`badge badge-${isActive ? 'success' : 'warning'}`}><span className="badge-dot" /> {isActive ? 'Live' : 'Pending'}</span>
                <span className={`badge badge-${sslValid ? 'success' : 'warning'}`}><IcShield /> {sslValid ? 'Secure' : 'Securing…'}</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-soft)', marginTop: 6 }}>
                {isActive
                  ? <>Connected and serving traffic — nothing for you to do.</>
                  : <>Finish DNS setup below to take <strong style={{ color: 'var(--text)' }}>{domainName}</strong> live.</>}
              </div>
            </div>
            <button className="btn" onClick={runCheck} disabled={!primaryDomain?.id || checking}>
              <IcRefresh spin={checking} /> {checking ? 'Checking…' : 'Check connection'}
            </button>
          </div>
          <div style={{ position: 'absolute', right: -50, top: -50, width: 180, height: 180, borderRadius: '50%', background: 'var(--accent-soft)', pointerEvents: 'none', filter: 'blur(40px)' }} />
        </div>

        {/* Health checks (real status only) */}
        <div className="bento" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
          {(() => {
            const ok = checkResult ? checkResult.ok : dnsOk
            const sub = checkResult
              ? (ok ? `All ${checkResult.records.length} records found on your nameservers` : `${checkResult.records.filter(r => r.status !== 'ok').length} record(s) not visible yet`)
              : (dnsOk ? 'Run a check to confirm propagation' : 'Finish DNS setup to go live')
            return <ClHealth title="DNS records" value={ok ? 'Healthy' : 'Action needed'} sub={sub} ok={ok} warn={!ok} />
          })()}
          <ClHealth title="SSL certificate" value={sslValid ? 'Valid' : 'Pending'} sub={sslValid ? (sslDays != null ? `Renews automatically in ${sslDays} days` : `Secured automatically by ${cmsName}`) : 'Issuing certificate…'} ok={sslValid} warn={!sslValid} />
          <ClHealth title="Connection" value={isActive ? 'Live' : 'Pending'} sub={isActive ? 'Serving traffic worldwide' : 'Awaiting DNS verification'} ok={isActive} warn={!isActive} />
        </div>

        <div className="bento" style={{ gridTemplateColumns: '1.5fr 1fr', gap: 14, alignItems: 'flex-start' }}>
          {/* DNS records */}
          <div className="card card-pad" id="dns-records-card">
            <div className="flex items-center justify-between" style={{ marginBottom: 4, gap: 10 }}>
              <h2 className="h2">DNS records</h2>
              <div className="flex items-center gap-2">
                {checkResult
                  ? <span className={`badge badge-${checkResult.ok ? 'success' : 'warning'}`}>{checkResult.ok ? <IcCheck /> : <IcHelp />} {checkResult.ok ? 'All verified' : 'Action needed'}</span>
                  : <span className="badge">Not checked yet</span>}
                <button className="btn btn-sm" onClick={() => setShowGuide(true)}><IcBook /> Setup guide</button>
              </div>
            </div>
            <div className="text-muted" style={{ fontSize: 12.5, marginBottom: 14 }}>Managed by {cmsName} — you don't need to touch these.</div>
            <div className="sites-table" style={{ background: 'var(--bg-2)' }}>
              <div className="row header" style={{ gridTemplateColumns: '0.6fr 1fr 1.8fr auto' }}>
                <div>Type</div><div>Name</div><div>Value</div><div>Status</div>
              </div>
              {tableRows.map((r, i) => {
                const b = r.status && STATUS_BADGE[r.status]
                return (
                  <div className="row" key={i} style={{ gridTemplateColumns: '0.6fr 1fr 1.8fr auto' }}>
                    <div><span className="badge" style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5 }}>{r.type}</span></div>
                    <div style={{ fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-soft)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.value}</div>
                    <div>{b
                      ? <span className={`badge ${b.cls}`}>{b.icon} {b.label}</span>
                      : <span className="badge" style={{ color: 'var(--text-faint)' }}>—</span>}</div>
                  </div>
                )
              })}
            </div>

            {/* Contextual help right when a check finds problems */}
            {checkResult && failingRecords.length > 0 && (
              <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, background: 'var(--warning-soft)', border: '1px solid var(--border)' }}>
                <span className="icon-tile warning" style={{ width: 30, height: 30, fontSize: 13, flexShrink: 0 }}><IcHelp /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{failingRecords.length} record{failingRecords.length > 1 ? 's' : ''} not detected yet</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>Add them at your registrar — the setup guide walks you through it.</div>
                </div>
                <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }} onClick={() => setShowGuide(true)}><IcBook /> Open setup guide</button>
              </div>
            )}

            <div className="flex items-center justify-between" style={{ marginTop: 14, gap: 10 }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {checkResult
                  ? `Last checked just now${checkResult.nameservers?.length ? ` · via ${checkResult.nameservers[0]}` : ''}`
                  : 'Run a check to confirm your records are live on your nameservers.'}
              </span>
              <button className="btn btn-sm" onClick={runCheck} disabled={!primaryDomain?.id || checking}>
                <IcRefresh spin={checking} /> {checking ? 'Checking…' : 'Check connection'}
              </button>
            </div>
          </div>

          {/* Connected domains (real — list + disconnect) */}
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="icon-tile" style={{ width: 32, height: 32 }}><IcGlobe /></span>
                <h2 className="h2">Connected domains</h2>
              </div>
              <span className="badge">{domains.length}</span>
            </div>

            {domains.length === 0 ? (
              <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 12.5 }}>
                No domains connected yet — connect one to point it at your site.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {sortedDomains.map((d, i) => {
                  // Mirror the backend rule: the default domain (primary, or the
                  // only domain) is protected from deletion in the portal.
                  const isDefault = Boolean(d.is_primary) || domains.length <= 1
                  const isConnected = d.status === 'active' || d.status === 'connected'
                  return (
                    <div key={d.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', borderBottom: i < sortedDomains.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <span style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.domain || d.name}</span>
                      {isDefault && <span className="badge badge-accent">Default</span>}
                      <span className={`badge badge-${isConnected ? 'success' : 'warning'}`}>{d.status || 'pending'}</span>
                      {isDefault ? (
                        <span title="Your default domain is protected from deletion" style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--text-faint)', padding: 6 }}><IcLock /></span>
                      ) : (
                        <>
                          <button
                            className="btn btn-sm"
                            onClick={() => isConnected && setPrimaryMutation.mutate(d.id)}
                            disabled={!isConnected || setPrimaryMutation.isLoading}
                            title={isConnected ? 'Make this your default domain' : 'Connect this domain first to make it your default'}
                          >
                            <IcStar /> Make default
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => disconnect(d)} disabled={deleteDomainMutation.isLoading} title="Disconnect domain"><IcTrash /></button>
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            <button className="btn btn-sm" style={{ width: '100%', marginTop: 14, justifyContent: 'center' }} onClick={() => setShowAddDomain(true)}>
              <IcPlus /> Connect another domain
            </button>
          </div>
        </div>
      </div>

      {/* DNS setup guide modal */}
      {showGuide && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)' }} onClick={() => setShowGuide(false)} />
          <div className="card card-pad" style={{ position: 'relative', width: '100%', maxWidth: 560, zIndex: 1, maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
              <span className="icon-tile" style={{ width: 38, height: 38, fontSize: 16 }}><IcBook /></span>
              <div style={{ flex: 1 }}>
                <h2 className="h2">DNS setup guide</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 3 }}>Point <strong style={{ color: 'var(--text)' }}>{domainName}</strong> at {cmsName} — takes about 5 minutes, then DNS can take up to 24 hours to propagate.</div>
              </div>
              <button className="btn btn-sm btn-icon" onClick={() => setShowGuide(false)} title="Close"><IcX /></button>
            </div>

            {/* Steps */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
              {[
                'Log in to your domain registrar (GoDaddy, Namecheap, Cloudflare, etc.).',
                `Open the DNS settings for ${domainName}.`,
                'Add each record below exactly as shown (use the copy buttons).',
                'Save, then come back and hit “Check connection” — we verify it for you.',
              ].map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: 'var(--accent-soft)', color: 'var(--accent-3)', fontSize: 11.5, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                  <span style={{ fontSize: 13, color: 'var(--text-soft)', lineHeight: 1.5 }}>{s}</span>
                </div>
              ))}
            </div>

            <div className="eyebrow" style={{ marginBottom: 8 }}>Records to add</div>
            <div className="sites-table" style={{ background: 'var(--bg-2)' }}>
              <div className="row header" style={{ gridTemplateColumns: '0.5fr 1fr 1.8fr 0.5fr auto' }}>
                <div>Type</div><div>Name</div><div>Value</div><div>TTL</div><div></div>
              </div>
              {setupRecords.map((r, i) => (
                <div className="row" key={i} style={{ gridTemplateColumns: '0.5fr 1fr 1.8fr 0.5fr auto' }}>
                  <div><span className="badge" style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5 }}>{r.type}</span></div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.name}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-soft)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.value}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', fontSize: 12 }}>{r.ttl || 'auto'}</div>
                  <div>
                    <button className="btn btn-sm btn-icon" title="Copy value" onClick={() => copyValue(i, r.value)}>
                      {copiedIdx === i ? <span style={{ color: 'var(--success)', display: 'inline-flex' }}><IcCheck /></span> : <IcCopy />}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)', marginTop: 12, lineHeight: 1.5 }}>
              <span className="badge-dot" style={{ background: 'var(--info)', flexShrink: 0 }} />
              Already added them? DNS changes can take up to 24 hours to show up worldwide.
            </div>

            <div className="flex gap-2" style={{ marginTop: 16 }}>
              <button
                className="btn btn-primary"
                style={{ flex: 1, justifyContent: 'center' }}
                disabled={!primaryDomain?.id || checking}
                onClick={() => { runCheck(); setShowGuide(false) }}
              >
                <IcRefresh spin={checking} /> {checking ? 'Checking…' : 'Check connection'}
              </button>
              <button className="btn" onClick={() => setShowGuide(false)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Add domain modal */}
      {showAddDomain && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)' }} onClick={() => setShowAddDomain(false)} />
          <div className="card card-pad" style={{ position: 'relative', width: '100%', maxWidth: 420, zIndex: 1 }}>
            <h2 className="h2" style={{ marginBottom: 14 }}>Connect a domain</h2>
            <div className="help-field" style={{ marginBottom: 14 }}>
              <label>Domain name</label>
              <input type="text" placeholder="example.com" value={newDomain} onChange={e => setNewDomain(e.target.value)} autoFocus />
            </div>
            <div className="flex gap-2">
              <button
                className="btn btn-primary"
                style={{ flex: 1, justifyContent: 'center' }}
                disabled={!newDomain.trim() || addDomainMutation.isLoading}
                onClick={() => addDomainMutation.mutate(newDomain.trim())}
              >
                {addDomainMutation.isLoading ? 'Connecting…' : 'Connect domain'}
              </button>
              <button className="btn" onClick={() => setShowAddDomain(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default DomainManagement
